import { FC, useState } from "react"
import { Globe, CheckCircle, Clock, ExternalLink, Copy } from "lucide-react"
import { PrimaryBtn } from "../../buttons"
import { api } from "../../../utils"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import { WEBFLOW_DOMAIN_ENDPOINT } from "../../../constants"

interface WebflowDomainResponse {
  status: "success" | "error"
  message?: string
  incoming_domain: string
  target_domain: string
  vhost: {
    incoming_address: string
    target_address: string
    exact_match: boolean
    redirect: boolean
    target_ports: string
  }
  dns: {
    change_id: string
    status: "PENDING" | "INSYNC"
    submitted_at: string
  }
}

type ResultData = Omit<WebflowDomainResponse, "status" | "message">

const WebflowDomainConfig: FC = () => {
  const dispatch = useAppDispatch()
  const [targetDomain, setTargetDomain] = useState<string>("")
  const [incomingDomain, setIncomingDomain] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<ResultData | null>(null)

  const canSubmit = targetDomain.trim().length > 0

  const handleSubmit = async () => {
    setIsLoading(true)
    setResult(null)

    try {
      const payload: { target_domain: string; incoming_domain?: string } = {
        target_domain: targetDomain.trim()
      }

      if (incomingDomain.trim()) {
        payload.incoming_domain = incomingDomain.trim()
      }

      const { data } = await api.post<WebflowDomainResponse>(
        WEBFLOW_DOMAIN_ENDPOINT,
        payload
      )

      if (data.status === "error") {
        throw new Error(data.message || "Failed to configure Webflow domain")
      }

      setResult({
        incoming_domain: data.incoming_domain,
        target_domain: data.target_domain,
        vhost: data.vhost,
        dns: data.dns
      })

      // Clear form on success
      setTargetDomain("")
      setIncomingDomain("")

      dispatch(
        pushToast({
          msg: `Webflow domain configured: ${data.incoming_domain}`,
          theme: "success",
          timeout: 5000
        })
      )
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to configure Webflow domain"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    dispatch(
      pushToast({
        msg: "Copied to clipboard",
        theme: "success",
        timeout: 2000
      })
    )
  }

  const clearResult = () => {
    setResult(null)
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Globe className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Webflow Domain Setup
            </h3>
            <p className="text-sm text-gray-500">
              Configure DNS and vhost for Webflow proxy domains
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Domain (Webflow):
            </label>
            <input
              type="text"
              value={targetDomain}
              onChange={e => setTargetDomain(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="loansteady-lp.webflow.io"
            />
            <p className="mt-1 text-xs text-gray-500">
              The Webflow subdomain to proxy
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Incoming Domain (Optional):
            </label>
            <input
              type="text"
              value={incomingDomain}
              onChange={e => setIncomingDomain(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="custom.bankingbridge.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank to auto-generate (e.g., loansteady.bankingbridge.com)
            </p>
          </div>
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Configuring..." : "Configure Domain"}
          </PrimaryBtn>
        </div>

        {result && (
          <div className="mt-4 border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <h4 className="text-sm font-medium text-green-800">
                  Domain Configured Successfully
                </h4>
              </div>
              <button
                onClick={clearResult}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Dismiss
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      Incoming Domain
                    </p>
                    <p className="text-sm font-mono text-gray-900">
                      {result.incoming_domain}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyToClipboard(result.incoming_domain)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                      title="Copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={`https://${result.incoming_domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">
                      Target Domain
                    </p>
                    <p className="text-sm font-mono text-gray-900">
                      {result.target_domain}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.target_domain)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="border-t border-neutral-200 pt-4">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  DNS Status
                </p>
                <div className="flex items-center gap-2">
                  {result.dns.status === "PENDING" ? (
                    <>
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-700">
                        Propagating...
                      </span>
                      <span className="text-xs text-gray-500">
                        (usually takes 1-2 minutes)
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-700">
                        DNS Synchronized
                      </span>
                    </>
                  )}
                </div>
                {result.dns.submitted_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted:{" "}
                    {new Date(result.dns.submitted_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default WebflowDomainConfig
